import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { logError } from "@/lib/log";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Siren, Loader2, Send } from "lucide-react";
import { toast } from "sonner";

const PRESETS = [
  { title: "Inspector on site", body: "The inspector has arrived. Follow the Inspection Day plan and speak openly and honestly." },
  { title: "Team briefing today", body: "Short compliance briefing at handover — please make sure your training records are up to date." },
];

export default function TeamAlert() {
  const [form, setForm] = useState({ title: "", body: "" });
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/team/alerts").then((r) => setHistory(r.data)).catch((err) => logError("Alert history failed", err));
  useEffect(() => { load(); }, []);

  const send = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/team/alert", { ...form, link: "/inspection-day" });
      toast.success(data.message); setForm({ title: "", body: "" }); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't send alert"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-6 shadow-sm" data-testid="team-alert-card">
      <h2 className="font-display font-bold text-lg flex items-center gap-2 mb-1"><Siren className="w-5 h-5 text-destructive" /> Push alert to your team</h2>
      <p className="text-sm text-muted-foreground mb-4">Sends an in-app notification and a phone push to every team member who has notifications on.</p>
      <div className="flex flex-wrap gap-2 mb-3">
        {PRESETS.map((p) => (
          <button key={p.title} type="button" onClick={() => setForm(p)} className="text-xs px-3 py-1.5 rounded-full border border-border hover:bg-secondary" data-testid={`team-alert-preset-${p.title.toLowerCase().replace(/\s+/g, "-")}`}>{p.title}</button>
        ))}
      </div>
      <form onSubmit={send} className="grid gap-3" data-testid="team-alert-form">
        <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required minLength={3} maxLength={80} placeholder="Alert title" data-testid="team-alert-title" />
        <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} required minLength={3} maxLength={300} rows={2} placeholder="Message to the team" data-testid="team-alert-body" />
        <div><Button type="submit" disabled={busy} data-testid="team-alert-send">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" /> Send alert</>}</Button></div>
      </form>
      {history.length > 0 && (
        <ul className="mt-5 space-y-2 text-sm" data-testid="team-alert-history">
          {history.slice(0, 5).map((h) => (
            <li key={h.id} className="flex justify-between gap-3 border-t border-border pt-2">
              <span><span className="font-medium">{h.title}</span> <span className="text-muted-foreground">— {h.body}</span></span>
              <span className="text-xs text-muted-foreground shrink-0">{h.recipients} sent</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
